import { type AppState } from "@/store";
import { getInfoCoupon } from "@/utils/cartV2";
import { ECheckoutSource, getProductSource } from "@/utils/checkoutType";
import { checkProductHasStock } from "@/utils/product";
import { createSelector } from "@reduxjs/toolkit";

interface SelectorProps {
  allSources?: boolean;
  source?: ECheckoutSource;
}

const originalPrice = (item: Cart) => item?.price?.originalPrice ?? 0;
const discountedPrice = (item: Cart) => item?.price?.finalPrice ?? 0;
const maxQtyToOrder = (item: Cart) => item?.details.maxQtyToOrder ?? 0;
const hasDiscount = (item: Cart) => originalPrice(item) - discountedPrice(item) > 0;
const quantityOrMaxOrder = (item: Cart) =>
  item?.quantity > maxQtyToOrder(item) ? maxQtyToOrder(item) : item?.quantity;

// new selectors for the Split
const selectRawItems = (state: AppState) => state.cart.items ?? [];
const selectActiveSource = (state: AppState) => state.checkout.activeSource;

// base selector to insert props
const selectItems = createSelector(
  [selectRawItems, selectActiveSource, (_state: AppState, props?: SelectorProps) => props],
  (items, activeTypeFromRedux, props) => {
    const { allSources = true, source } = props ?? {};

    if (allSources) return items;

    // If the cartType is provided cart filter by it
    const targetType = source ?? activeTypeFromRedux;

    return items.filter((item) => getProductSource(item) === targetType);
  }
);

const selectTotalCountCart = createSelector([selectItems], (items) => {
  return items.reduce((acc, item) => {
    return acc + (item?.quantity ?? 0);
  }, 0);
});

const selectItemsWithoutDiscount = createSelector([selectItems], (items) => {
  return items.filter((item) => !hasDiscount(item));
});

const selectTotalItemsWithoutDiscount = createSelector([selectItemsWithoutDiscount], (items) => {
  return items.reduce((acc, item) => {
    return acc + (quantityOrMaxOrder(item) ?? 0) * originalPrice(item);
  }, 0);
});

const selectItemsWithDiscount = createSelector([selectItems], (items) => {
  return items.filter((item) => hasDiscount(item));
});

const selectSubTotal = createSelector([selectItems], (items) =>
  items.reduce((acc, item) => {
    return acc + (quantityOrMaxOrder(item) ?? 0) * originalPrice(item);
  }, 0)
);

const selectDiscount = createSelector([selectItems], (items) =>
  items.reduce((acc, item) => {
    return (
      acc +
      (hasDiscount(item) ? originalPrice(item) - discountedPrice(item) : 0) * (quantityOrMaxOrder(item) ?? 1)
    );
  }, 0)
);

const selectCoupon = createSelector(
  [(state: AppState) => state.checkout.coupon, selectTotalItemsWithoutDiscount],
  (coupon, totalItemsWithoutDiscount) => {
    return getInfoCoupon(coupon, totalItemsWithoutDiscount);
  }
);

const selectProcessedItems = createSelector(
  [selectItems, selectCoupon, selectTotalItemsWithoutDiscount],
  (items, coupon, totalItemsWithoutDiscount) => {
    const discountValue = coupon?.finalValuePrice ?? 0;

    return items.reduce<ProcessedItems[]>((acc, item) => {
      if (item.details.customLabel) {
        return [
          ...acc,
          {
            ...item,
            price: {
              ...item.price,
              coupon: undefined,
              discountType: "customLabel",
            },
          },
        ];
      }

      if (item.details.onSale) {
        return [
          ...acc,
          {
            ...item,
            price: {
              ...item.price,
              coupon: undefined,
              discountType: "promofit",
            },
          },
        ];
      }

      if (!coupon?.isValid || item.quantity === 0) {
        return [
          ...acc,
          {
            ...item,
            price: {
              ...item.price,
              coupon: undefined,
              discountType: "",
            },
          },
        ];
      }

      const itemTotalPrice = discountedPrice(item) * quantityOrMaxOrder(item);

      const proportion = itemTotalPrice / totalItemsWithoutDiscount;

      // Calculate the discount amount for the item based on its proportion
      let itemDiscountAmount = discountValue * proportion;

      // Check if the discount exceeds the maximum discount per item (which is the item's total price)
      const maxDiscountPerItem = itemTotalPrice;

      itemDiscountAmount = Math.min(itemDiscountAmount, maxDiscountPerItem);

      // Apply the discount to the item's unit price
      const discountPerUnit = itemDiscountAmount / item.quantity;

      const finalPrice = discountedPrice(item) - discountPerUnit;
      const discount = originalPrice(item) - finalPrice;

      return [
        ...acc,
        {
          ...item,
          price: {
            ...item.price,
            coupon: {
              type: coupon?.discountValueDisplay ?? "",
              discountValue,
              discountPerUnit,
              gross: discountValue,
            },
            discount,
            discountType: "coupon",
            finalPrice: discountedPrice(item) - discountPerUnit,
          },
        },
      ];
    }, []);
  }
);

const selectTotal = createSelector([selectSubTotal, selectDiscount], (subTotal, discount) => {
  return subTotal - discount;
});

const selectProcessedItemsBySource = createSelector([selectProcessedItems], (processedItems) => {
  return processedItems.reduce(
    (acc, item) => {
      const source = getProductSource(item);
      acc[source].push(item);
      return acc;
    },
    {
      [ECheckoutSource.LOCAL]: [] as ProcessedItems[],
      [ECheckoutSource.INTERNATIONAL]: [] as ProcessedItems[],
    }
  );
});

const selectHasCheckoutCartSplit = createSelector([selectProcessedItemsBySource], (itemsByType) => {
  return itemsByType.local.length > 0 && itemsByType.international.length > 0;
});

const selectLocation = createSelector([(state: AppState) => state.checkout], (checkout) => {
  if (checkout.pickupStore) {
    return {
      isPickup: true,
      ...checkout.pickupStore,
    };
  }

  return {
    isPickup: false,
    ...checkout.address,
  };
});

const selectItemsStock = createSelector([selectItems], (items) =>
  items.reduce((acc, item) => {
    return {
      ...acc,
      [item.product.sku]: checkProductHasStock(item),
    };
  }, {})
);

export {
  selectItems,
  selectItemsWithoutDiscount,
  selectItemsWithDiscount,
  selectTotalItemsWithoutDiscount,
  selectSubTotal,
  selectDiscount,
  selectCoupon,
  selectProcessedItems,
  selectTotal,
  selectLocation,
  originalPrice,
  discountedPrice,
  maxQtyToOrder,
  quantityOrMaxOrder,
  selectItemsStock,
  selectTotalCountCart,
  selectActiveSource,
  selectHasCheckoutCartSplit,
  selectProcessedItemsBySource,
};
